import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { 
  Video, 
  Download, 
  Play,
  Pause,
  Sparkles,
  Copy,
  Share2,
  Maximize2,
  Clock,
  Film,
  Coins,
  AlertTriangle,
  Settings,
  ChevronDown,
  X,
  CreditCard,
  ChevronRight,
  Trash2,
  Upload,
  Image as ImageIcon
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { generateVideo } from '../services/falApi';
import { downloadFile, copyToClipboard, shareContent } from '../utils';
import { useCredits } from '../hooks/useCredits';
import { useModels } from '../hooks/useModels';
import ModelPicker from './ModelPicker';

const videoSchema = z.object({
  prompt: z.string().min(1, "Il prompt è obbligatorio"),
  model: z.string().default("fal-ai/minimax-video-01"),
  duration: z.number().min(1).max(15).default(5),
  aspectRatio: z.string().default("16:9"),
  fps: z.number().min(12).max(30).default(24),
  seed: z.number().optional(),
  imageInput: z.any().optional(),
  strength: z.number().min(0.1).max(1).default(0.8)
});

export default function VideoGenerator() {
  const [generatedVideos, setGeneratedVideos] = useState([]);
  const [error, setError] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedVideoIndex, setSelectedVideoIndex] = useState(null);
  const [playingVideos, setPlayingVideos] = useState(new Set());
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [showCreditsBanner, setShowCreditsBanner] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [endImageFile, setEndImageFile] = useState(null);
  const [endImagePreview, setEndImagePreview] = useState(null);
  const [generateAudio, setGenerateAudio] = useState(true);
  const [videoResolution, setVideoResolution] = useState('720p');
  const [useAutoDuration, setUseAutoDuration] = useState(false);
  const [mobileTab, setMobileTab] = useState('form');

  const { credits, refreshCredits } = useCredits();
  const { videoModels } = useModels();
  const navigate = useNavigate();
  
  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm({
    resolver: zodResolver(videoSchema),
    defaultValues: {
      model: "fal-ai/minimax-video-01",
      duration: 5,
      aspectRatio: "16:9",
      fps: 24,
      strength: 0.8
    }
  });

  const selectedModel = watch('model');
  const currentModelData = videoModels.find(m => m.id === selectedModel);
  const requiredCredits = currentModelData?.credits || 50;
  const supportsImageInput = currentModelData?.supportsImageInput || false;

  // reset per-model states on model switch
  useEffect(() => {
    if (!supportsImageInput && uploadedImage) removeImage();
    if (!currentModelData?.supportsEndFrame) { setEndImageFile(null); setEndImagePreview(null); }
    setGenerateAudio(true);
    setUseAutoDuration(currentModelData?.supportsAutoDuration ? true : false);
    if (currentModelData?.supportedResolutions) setVideoResolution(currentModelData.supportedResolutions[1] || currentModelData.supportedResolutions[0]);
    const ratios = currentModelData?.supportedAspectRatios;
    if (ratios) {
      const defaultRatio = ratios.includes('16:9') ? '16:9' : ratios[0];
      setValue('aspectRatio', defaultRatio);
    }
    const min = currentModelData?.minDuration || 1;
    const max = currentModelData?.maxDuration || 10;
    setValue('duration', Math.min(5, max));
    if (min > watch('duration')) setValue('duration', min);
  }, [selectedModel]);

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Verifica tipo file
      if (!file.type.startsWith('image/')) {
        setError('Per favore seleziona un file immagine valido');
        return;
      }
      
      // Verifica dimensione (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError('L\'immagine deve essere inferiore a 10MB');
        return;
      }
      
      setUploadedImage(file);
      setValue('imageInput', file);
      
      // Crea preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
      
      setError(null);
    }
  };

  const removeImage = () => {
    setUploadedImage(null);
    setImagePreview(null);
    setValue('imageInput', null);
    const fileInput = document.getElementById('image-upload');
    if (fileInput) fileInput.value = '';
  };

  const handleEndImageUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { setError('Seleziona un file immagine valido'); return; }
    if (file.size > 10 * 1024 * 1024) { setError('L\'immagine deve essere inferiore a 10MB'); return; }
    setEndImageFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setEndImagePreview(e.target.result);
    reader.readAsDataURL(file);
    setError(null);
  };

  const removeEndImage = () => {
    setEndImageFile(null);
    setEndImagePreview(null);
    const fi = document.getElementById('end-image-upload');
    if (fi) fi.value = '';
  };

  const downloadVideo = async (videoUrl, index) => {
    try {
      const response = await fetch(videoUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      downloadFile(url, `ai-generated-video-${Date.now()}-${index + 1}.mp4`);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Errore nel download:', error);
    }
  };

  const copyVideoUrl = async (videoUrl) => {
    try {
      await copyToClipboard(videoUrl);
    } catch (error) {
      console.error('Errore nella copia:', error);
    }
  };

  const shareVideo = async (videoUrl) => {
    try {
      await shareContent({
        title: 'Video generato con AI',
        url: videoUrl
      });
    } catch (error) {
      console.error('Errore nella condivisione:', error);
      copyVideoUrl(videoUrl);
    }
  };

  const generateRandomSeed = () => {
    const randomSeed = Math.floor(Math.random() * 1000000);
    setValue("seed", randomSeed);
  };

  const toggleVideoPlay = (index) => {
    const video = document.getElementById(`video-${index}`);
    if (video) {
      if (playingVideos.has(index)) {
        video.pause();
        setPlayingVideos(prev => {
          const newSet = new Set(prev);
          newSet.delete(index);
          return newSet;
        });
      } else {
        video.play();
        setPlayingVideos(prev => new Set([...prev, index]));
      }
    }
  };

  const onSubmit = async (data) => {
    // Protezione contro chiamate multiple
    if (isGenerating) {
      console.log('⚠️ Generazione già in corso, ignorando chiamata duplicata');
      return;
    }

    console.log('🚀 Iniziando generazione video:', { model: data.model });
    setMobileTab('results');
    setIsGenerating(true);
    setError(null);
    setShowCreditsBanner(false);
    
    // Client-side credit guard (UX only — backend enforces server-side)
    if (credits < requiredCredits) {
      setIsGenerating(false);
      setShowCreditsBanner(true);
      return;
    }

    try {
      const toBase64 = (file) => new Promise((resolve) => {
        const r = new FileReader();
        r.onload = (e) => resolve(e.target.result);
        r.readAsDataURL(file);
      });

      const generateParams = {
        prompt: data.prompt,
        model: data.model,
        duration: useAutoDuration ? 'auto' : data.duration,
        aspect_ratio: data.aspectRatio,
        fps: data.fps,
        seed: data.seed,
        ...(currentModelData?.supportedResolutions && { resolution: videoResolution }),
        ...(currentModelData?.supportsAudioToggle && { generate_audio: generateAudio }),
      };

      if (supportsImageInput && uploadedImage) {
        generateParams.image_url = await toBase64(uploadedImage);
        if (!currentModelData?.isReferenceToVideo) generateParams.strength = data.strength;
      }

      if (currentModelData?.supportsEndFrame && endImageFile) {
        generateParams.end_image_url = await toBase64(endImageFile);
      }

      // Backend deducts credits atomically — no separate /credits/deduct call needed
      const result = await generateVideo(generateParams);

      if (result.success && result.data?.video_url) {
        setGeneratedVideos(prev => [{
          url: result.data.video_url,
          prompt: data.prompt,
          duration: data.duration,
          aspectRatio: data.aspectRatio,
          fps: data.fps,
          timestamp: Date.now(),
          creditsUsed: requiredCredits,
          hasImageInput: !!(supportsImageInput && uploadedImage),
          strength: data.strength
        }, ...prev]);
        refreshCredits();
      } else {
        if (result.error?.includes('insufficienti')) {
          setShowCreditsBanner(true);
        }
        throw new Error(result.error || 'Errore nella generazione del video');
      }

    } catch (error) {
      console.error("Errore nella generazione:", error);
      setError(error.message || 'Errore sconosciuto');
      refreshCredits();
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Mobile Tab Switcher */}
      <div className="xl:hidden mb-4 flex p-1 rounded-2xl" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
        <button
          type="button"
          onClick={() => setMobileTab('form')}
          className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-all ${mobileTab === 'form' ? 'pill-active' : ''}`}
          style={{ color: mobileTab === 'form' ? '#fff' : 'rgba(255,255,255,0.45)' }}
        >
          <Video size={14} />
          Genera
        </button>
        <button
          type="button"
          onClick={() => setMobileTab('results')}
          className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-all ${mobileTab === 'results' ? 'pill-active' : ''}`}
          style={{ color: mobileTab === 'results' ? '#fff' : 'rgba(255,255,255,0.45)' }}
        >
          <Film size={14} />
          Video Generati
          {generatedVideos.length > 0 && (
            <span className="ml-0.5 text-xs px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(255,255,255,0.2)' }}>{generatedVideos.length}</span>
          )}
        </button>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
      {/* Form Panel */}
      <div className={mobileTab === 'results' ? 'hidden xl:block' : ''}>
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5 }}
        className="space-y-6"
      >
        {/* Header Box */}
        <div className="panel rounded-3xl p-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <div className="p-3 icon-box primary rounded-2xl">
                <Video style={{ color: '#fff' }} size={22} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'var(--font-heading)', letterSpacing: '-0.025em' }}>Generatore Video</h2>
                <p className="text-sm" style={{ color: 'rgba(255,255,255,0.70)' }}>Crea video straordinari con l'AI</p>
              </div>
            </div>

            <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl" style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)' }}>
              <Coins style={{ color: 'rgba(255,255,255,0.90)' }} size={14} />
              <span className="text-white font-semibold text-sm">{credits}</span>
              <span className="text-xs" style={{ color: 'rgba(255,255,255,0.36)' }}>crediti</span>
            </div>
          </motion.div>
        </div>

        {/* Box Principale - Form */}
        <div className="panel rounded-3xl p-7">
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: -10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: -10 }}
                className="mb-6 p-4 error-banner rounded-2xl"
              >
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: 'rgba(255,80,80,0.9)' }} />
                  <p className="text-sm font-medium">{error}</p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Model Selection - SPOSTATO PRIMA DEL PROMPT */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
            >
              <label className="label-sm mb-3 block">Modello AI</label>
              <ModelPicker
                selectedModel={selectedModel}
                onModelSelect={(modelId) => setValue('model', modelId)}
                category="video"
                className="w-full"
              />
            </motion.div>

            {/* Image Upload Section - Solo se il modello supporta image input */}
            {supportsImageInput && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.27 }}
              >
                <label className="label-sm mb-3 block">
                  {currentModelData?.isReferenceToVideo ? 'Immagine di Riferimento' : currentModelData?.isImageToVideo ? 'Immagine di Partenza' : 'Immagine di Riferimento'}
                </label>

                {!imagePreview ? (
                  <div className="relative">
                    <input id="image-upload" type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                    <label
                      htmlFor="image-upload"
                      className="flex flex-col items-center justify-center w-full h-28 rounded-2xl cursor-pointer upload-area group"
                      style={{ border: '1px dashed rgba(255,255,255,0.25)', background: 'rgba(255,255,255,0.02)' }}
                    >
                      <div className="icon-box primary rounded-xl p-2 mb-2 group-hover:scale-105 transition-transform">
                        <Upload style={{ color: '#fff' }} size={18} />
                      </div>
                      <p className="text-sm text-center" style={{ color: 'rgba(255,255,255,0.6)' }}>
                        Clicca per caricare
                        <br /><span className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>PNG, JPG, WEBP · max 10MB</span>
                      </p>
                    </label>
                  </div>
                ) : (
                  <div className="relative">
                    <div className="relative w-full h-28 rounded-2xl overflow-hidden panel">
                      <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/20 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                        <button type="button" onClick={removeImage} className="p-2 rounded-full text-white icon-box">
                          <X size={14} />
                        </button>
                      </div>
                    </div>
                    <p className="text-xs mt-2 flex items-center gap-1" style={{ color: 'rgba(255,255,255,0.35)' }}>
                      <ImageIcon size={11} />
                      {uploadedImage?.name}
                    </p>
                  </div>
                )}

                {imagePreview && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="mt-4">
                    <label className="label-sm mb-3 block">
                      Influenza Immagine: {(watch("strength") * 100).toFixed(0)}%
                    </label>
                    <input
                      {...register("strength", { valueAsNumber: true })}
                      type="range" min="0.1" max="1" step="0.1"
                      className="w-full h-1.5 rounded-lg appearance-none cursor-pointer slider"
                      style={{ background: 'rgba(255,255,255,0.12)' }}
                    />
                    <div className="flex justify-between text-xs mt-1" style={{ color: 'rgba(255,255,255,0.30)' }}>
                      <span>Meno influenza</span><span>Più influenza</span>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            )}

            {/* End Frame Upload - solo per modelli con supportsEndFrame */}
            {currentModelData?.supportsEndFrame && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.29 }}>
                <label className="label-sm mb-3 block">Frame Finale (opzionale)</label>
                {!endImagePreview ? (
                  <div className="relative">
                    <input id="end-image-upload" type="file" accept="image/*" onChange={handleEndImageUpload} className="hidden" />
                    <label htmlFor="end-image-upload" className="flex flex-col items-center justify-center w-full h-20 rounded-2xl cursor-pointer upload-area group" style={{ border: '1px dashed rgba(255,255,255,0.20)', background: 'rgba(255,255,255,0.02)' }}>
                      <div className="flex items-center gap-2">
                        <Upload style={{ color: 'rgba(255,255,255,0.45)' }} size={15} />
                        <p className="text-xs" style={{ color: 'rgba(255,255,255,0.50)' }}>Frame finale · PNG, JPG, WEBP</p>
                      </div>
                    </label>
                  </div>
                ) : (
                  <div className="relative w-full h-20 rounded-2xl overflow-hidden panel">
                    <img src={endImagePreview} alt="End frame" className="w-full h-full object-cover" />
                    <button type="button" onClick={removeEndImage} className="absolute top-1.5 right-1.5 p-1 rounded-full text-white icon-box"><X size={12} /></button>
                  </div>
                )}
              </motion.div>
            )}

            {/* Prompt Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <label className="label-sm mb-3 block">Descrizione Video</label>
              <div className="relative">
                <textarea
                  {...register("prompt")}
                  placeholder={supportsImageInput && imagePreview ?
                    "Descrivi come vuoi che l'immagine si animi..." :
                    "Descrivi il video che vuoi generare..."
                  }
                  className="input-field w-full h-32 px-5 py-4 rounded-2xl resize-none overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
                />
                <div className="absolute bottom-3 right-3 text-xs" style={{ color: 'rgba(255,255,255,0.22)' }}>
                  {watch("prompt")?.length || 0} caratteri
                </div>
              </div>
              {errors.prompt && (
                <motion.p 
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="text-red-400 text-sm mt-2 flex items-center gap-1"
                >
                  <div className="w-1 h-1 bg-red-400 rounded-full" />
                  {errors.prompt.message}
                </motion.p>
              )}
            </motion.div>

            {/* Advanced Options Collapsible Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="mt-6"
            >
              <motion.button
                type="button"
                onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
                className="pill-ghost w-full flex items-center justify-between p-4 rounded-2xl"
                whileHover={{ scale: 1.005 }}
                whileTap={{ scale: 0.99 }}
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 icon-box rounded-xl">
                    <Settings style={{ color: 'rgba(255,255,255,0.65)' }} size={17} />
                  </div>
                  <div className="text-left">
                    <h3 className="text-white font-semibold text-sm">Opzioni Avanzate</h3>
                    <p className="text-xs" style={{ color: 'rgba(255,255,255,0.36)' }}>Durata, formato, FPS e seed</p>
                  </div>
                </div>
                <motion.div
                  animate={{ rotate: showAdvancedOptions ? 180 : 0 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                  style={{ color: 'rgba(255,255,255,0.40)' }}
                >
                  <ChevronDown size={18} />
                </motion.div>
              </motion.button>

              <AnimatePresence>
                {showAdvancedOptions && (
                  <motion.div
                    initial={{ opacity: 0, height: 0, y: -10 }}
                    animate={{ opacity: 1, height: "auto", y: 0 }}
                    exit={{ opacity: 0, height: 0, y: -10 }}
                    transition={{ duration: 0.4, ease: "easeInOut" }}
                    className="overflow-hidden"
                  >
                    <div className="mt-4 space-y-6 p-6 rounded-2xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                      
                        {/* Resolution picker - solo per modelli con supportedResolutions */}
                      {currentModelData?.supportedResolutions && (
                        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
                          <label className="label-sm block mb-3">Risoluzione</label>
                          <div className="flex gap-2">
                            {currentModelData.supportedResolutions.map((res) => (
                              <button key={res} type="button" onClick={() => setVideoResolution(res)}
                                className={`flex-1 py-2 rounded-xl text-sm font-medium transition-all ${videoResolution === res ? 'pill-active' : 'pill-ghost'}`}>
                                {res}
                              </button>
                            ))}
                          </div>
                        </motion.div>
                      )}

                      {/* Generate Audio toggle - solo per modelli Seedance 2.0 */}
                      {currentModelData?.supportsAudioToggle && (
                        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }}>
                          <button type="button" onClick={() => setGenerateAudio(!generateAudio)}
                            className={`w-full flex items-center justify-between p-4 rounded-xl transition-all ${generateAudio ? 'pill-active' : 'pill-ghost'}`}>
                            <div className="flex items-center gap-3">
                              <span className="text-sm font-medium">Audio Sincronizzato</span>
                              <span className="text-xs opacity-60">Effetti sonori e parlato</span>
                            </div>
                            <div className={`w-10 h-5 rounded-full transition-all relative ${generateAudio ? 'bg-blue-500' : 'bg-white/15'}`}>
                              <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${generateAudio ? 'left-5' : 'left-0.5'}`} />
                            </div>
                          </button>
                        </motion.div>
                      )}

                      {/* Duration and FPS */}
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="grid grid-cols-2 gap-4"
                      >
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <label className="label-sm">
                              {useAutoDuration ? 'Durata: Auto' : `Durata: ${watch("duration")}s`}
                            </label>
                            {currentModelData?.supportsAutoDuration && (
                              <button type="button" onClick={() => setUseAutoDuration(!useAutoDuration)}
                                className={`text-xs px-2 py-0.5 rounded-lg transition-all ${useAutoDuration ? 'pill-active' : 'pill-ghost'}`}>
                                Auto
                              </button>
                            )}
                          </div>
                          {!useAutoDuration && (
                            <>
                              <input
                                {...register("duration", { valueAsNumber: true })}
                                type="range"
                                min={currentModelData?.minDuration || 1}
                                max={currentModelData?.maxDuration || 10}
                                className="w-full h-1.5 rounded-lg appearance-none cursor-pointer slider" style={{ background: 'rgba(255,255,255,0.12)' }}
                              />
                              <div className="flex justify-between text-xs mt-1" style={{ color: 'rgba(255,255,255,0.32)' }}>
                                <span>{currentModelData?.minDuration || 1}s</span>
                                <span>{currentModelData?.maxDuration || 10}s</span>
                              </div>
                            </>
                          )}
                        </div>

                        {currentModelData?.supportsFps !== false && (
                          <div>
                            <label className="label-sm block mb-3">FPS: {watch("fps")}</label>
                            <input
                              {...register("fps", { valueAsNumber: true })}
                              type="range"
                              min="12"
                              max="30"
                              className="w-full h-1.5 rounded-lg appearance-none cursor-pointer slider" style={{ background: 'rgba(255,255,255,0.12)' }}
                            />
                            <div className="flex justify-between text-xs mt-1" style={{ color: 'rgba(255,255,255,0.32)' }}>
                              <span>12</span>
                              <span>30</span>
                            </div>
                          </div>
                        )}
                      </motion.div>

                      {/* Aspect Ratio */}
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                      >
                        <label className="label-sm block mb-3">Formato Video</label>
                        <div className="flex flex-wrap gap-2">
                          {(currentModelData?.supportedAspectRatios || ["16:9", "9:16", "1:1"]).map((ratio) => (
                            <motion.button
                              key={ratio}
                              type="button"
                              onClick={() => setValue("aspectRatio", ratio)}
                              className={`px-3 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${watch("aspectRatio") === ratio ? "pill-active" : "pill-ghost"}`}
                              whileHover={{ scale: 1.04 }}
                              whileTap={{ scale: 0.96 }}
                            >
                              {ratio}
                            </motion.button>
                          ))}
                        </div>
                      </motion.div>

                      {/* Seed Section */}
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                      >
                        <label className="label-sm block mb-3">Seed (opzionale)</label>
                        <div className="flex gap-3">
                          <input
                            {...register("seed", { valueAsNumber: true })}
                            type="number"
                            placeholder="Lascia vuoto per casuale"
                            className="input-field flex-1 px-4 py-3 rounded-2xl"
                          />
                          <motion.button
                            type="button"
                            onClick={generateRandomSeed}
                            className="icon-box px-4 py-3 rounded-2xl text-white transition-all duration-200"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            title="Genera seed casuale"
                          >
                            <Sparkles size={20} />
                          </motion.button>
                        </div>
                      </motion.div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>

            {/* Generate Button */}
            <motion.button
              type="submit"
              disabled={isGenerating}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`py-4 px-6 rounded-2xl text-sm ${isGenerating ? 'generate-btn-loading' : 'generate-btn'}`}
            >
              {isGenerating ? (
                <>
                  <div className="w-4 h-4 border-2 rounded-full animate-spin" style={{ borderColor: 'rgba(255,255,255,0.15)', borderTopColor: 'rgba(255,255,255,0.5)' }} />
                  Generazione in corso...
                </>
              ) : (
                <>
                  <Sparkles size={18} />
                  Genera Video ({requiredCredits} crediti)
                </>
              )}
            </motion.button>
          </form>
        </div>
      </motion.div>
      </div>

      <div className={mobileTab === 'form' ? 'hidden xl:block' : ''}>
      {/* Results Panel */}
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="space-y-6"
      >
        {/* Banner Crediti Insufficienti - Versione Compatta per Video */}
        <AnimatePresence>
          {showCreditsBanner && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ type: "spring", damping: 25, stiffness: 400 }}
              className="relative mb-4"
            >
              <div className="error-banner rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="icon-box w-8 h-8 rounded-lg flex-shrink-0">
                      <AlertTriangle size={15} style={{ color: 'rgba(255,130,130,1)' }} />
                    </div>
                    <div>
                      <p className="text-white font-medium text-sm">
                        Crediti insufficienti ({credits}/{requiredCredits})
                      </p>
                      <p className="text-xs" style={{ color: 'rgba(255,130,130,0.65)' }}>
                        Ricarica per continuare a creare video
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <motion.button
                      onClick={() => navigate('/credits')}
                      className="pill-active px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <CreditCard size={12} />
                      Ricarica
                    </motion.button>
                    <motion.button
                      onClick={() => setShowCreditsBanner(false)}
                      className="p-1 transition-colors rounded"
                      style={{ color: 'rgba(255,255,255,0.4)' }}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <X size={14} />
                    </motion.button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Video Gallery */}
        <div className="panel rounded-3xl p-8">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-white flex items-center gap-3">
              <span className="icon-box w-9 h-9 rounded-xl"><Video size={18} /></span>
              Video Generati
            </h3>
            {generatedVideos.length > 0 && (
              <motion.button
                onClick={() => setGeneratedVideos([])}
                className="pill-ghost px-4 py-2 rounded-xl text-sm flex items-center gap-2"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Trash2 size={16} />
                Cancella Tutto
              </motion.button>
            )}
          </div>
          
          {generatedVideos.length === 0 ? (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-16"
            >
              {isGenerating ? (
                // Animazione di caricamento video
                <motion.div className="space-y-8">
                  {/* Anelli rotanti per video */}
                  <div className="relative w-32 h-32 mx-auto">
                    {[...Array(3)].map((_, i) => (
                      <motion.div
                        key={i}
                        className="absolute inset-0 border-4 rounded-full"
                        style={{
                          width: `${100 - i * 20}%`,
                          height: `${100 - i * 20}%`,
                          margin: `${i * 10}%`,
                          borderColor: `rgba(255,255,255,${0.06 - i * 0.01})`,
                          borderTopColor: `rgba(255,255,255,${0.55 - i * 0.12})`
                        }}
                        animate={{ rotate: 360 }}
                        transition={{
                          duration: 2 + i * 0.5,
                          repeat: Infinity,
                          ease: "linear"
                        }}
                      />
                    ))}

                    <motion.div
                      className="absolute inset-0 flex items-center justify-center"
                      animate={{ scale: [1, 1.15, 1], opacity: [0.6, 1, 0.6] }}
                      transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                    >
                      <div className="icon-box w-14 h-14 rounded-2xl">
                        <Film size={26} style={{ color: 'rgba(255,255,255,0.75)' }} />
                      </div>
                    </motion.div>
                  </div>
                  
                  {/* Testo animato per video */}
                  <motion.div
                    animate={{ 
                      opacity: [0.5, 1, 0.5]
                    }}
                    transition={{ 
                      duration: 1.5, 
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                    className="space-y-3"
                  >
                    <h4 className="text-2xl font-bold text-white">
                      Video in Creazione...
                    </h4>
                    <p className="max-w-sm mx-auto" style={{ color: 'rgba(255,255,255,0.42)' }}>
                      Il tuo video AI sta prendendo vita
                    </p>
                  </motion.div>
                  
                  {/* Particelle fluttuanti per video */}
                  <div className="absolute inset-0 pointer-events-none">
                    {[...Array(8)].map((_, i) => (
                      <motion.div
                        key={i}
                        className="absolute w-1.5 h-1.5 rounded-full"
                        style={{
                          background: 'rgba(255,255,255,0.35)',
                          left: `${15 + (i * 10)}%`,
                          top: `${25 + (i % 4) * 15}%`
                        }}
                        animate={{
                          y: [-10, -30, -10],
                          opacity: [0, 1, 0],
                          scale: [0.5, 1.2, 0.5]
                        }}
                        transition={{
                          duration: 3,
                          repeat: Infinity,
                          delay: i * 0.4,
                          ease: "easeInOut"
                        }}
                      />
                    ))}
                  </div>
                </motion.div>
              ) : (
                // Stato di default quando non si sta generando
                <>
                  <motion.div
                    animate={{ 
                      scale: [1, 1.1, 1],
                      opacity: [0.5, 1, 0.5]
                    }}
                    transition={{ 
                      duration: 3, 
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                    className="icon-box w-24 h-24 mx-auto mb-6 rounded-3xl"
                  >
                    <Video size={40} style={{ color: 'rgba(255,255,255,0.55)' }} />
                  </motion.div>
                  <h4 className="text-xl font-semibold text-white mb-2">Pronto per l'Azione?</h4>
                  <p className="max-w-sm mx-auto" style={{ color: 'rgba(255,255,255,0.38)' }}>
                    I tuoi incredibili video AI appariranno qui. Inizia descrivendo la tua visione!
                  </p>
                </>
              )}
            </motion.div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {generatedVideos.map((video, index) => (
                <motion.div
                  key={video.timestamp}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.1 }}
                  className="group relative rounded-2xl overflow-hidden transition-all duration-300" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)' }}
                >
                  <div className="relative aspect-video">
                    <video
                      id={`video-${index}`}
                      src={video.url}
                      className="w-full h-full object-cover"
                      loop
                      muted
                      playsInline
                      onPlay={() => setPlayingVideos(prev => new Set([...prev, index]))}
                      onPause={() => setPlayingVideos(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(index);
                        return newSet;
                      })}
                    />
                    
                    {/* Play/Pause Overlay */}
                    <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <motion.button
                        onClick={() => toggleVideoPlay(index)}
                        className="p-4 bg-white/20 backdrop-blur-sm rounded-full text-white hover:bg-white/30 transition-colors"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        {playingVideos.has(index) ? <Pause size={24} /> : <Play size={24} />}
                      </motion.button>
                    </div>
                    
                    {/* Badge per image-to-video */}
                    {video.hasImageInput && (
                      <div className="absolute top-2 left-2 px-2 py-1 rounded-lg text-xs font-medium flex items-center gap-1" style={{ background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(8px)', color: 'rgba(255,255,255,0.85)' }}>
                        <ImageIcon size={12} />
                        Image-to-Video
                      </div>
                    )}
                  </div>
                  
                  {/* Video Info */}
                  <div className="p-4">
                    <p className="text-white text-sm font-medium mb-2 line-clamp-2">
                      {video.prompt}
                    </p>
                    <div className="flex items-center justify-between text-xs mb-3" style={{ color: 'rgba(255,255,255,0.38)' }}>
                      <span>{video.duration}s • {video.fps}fps • {video.aspectRatio}</span>
                      <span>{video.creditsUsed} crediti</span>
                    </div>
                    {video.hasImageInput && (
                      <div className="text-xs mb-3" style={{ color: 'rgba(255,255,255,0.45)' }}>
                        Influenza: {(video.strength * 100).toFixed(0)}%
                      </div>
                    )}
                    
                    {/* Action Buttons */}
                    <div className="flex items-center gap-2">
                      <motion.button
                        onClick={() => downloadVideo(video.url, index)}
                        className="pill-ghost flex-1 px-3 py-2 rounded-lg text-xs flex items-center justify-center gap-1"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <Download size={14} />
                        Download
                      </motion.button>
                      
                      <motion.button
                        onClick={() => copyVideoUrl(video.url)}
                        className="pill-ghost px-3 py-2 rounded-lg text-xs"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        title="Copia URL"
                      >
                        <Copy size={14} />
                      </motion.button>

                      <motion.button
                        onClick={() => shareVideo(video.url)}
                        className="pill-ghost px-3 py-2 rounded-lg text-xs"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        title="Condividi"
                      >
                        <Share2 size={14} />
                      </motion.button>

                      <motion.button
                        onClick={() => setSelectedVideoIndex(index)}
                        className="pill-ghost px-3 py-2 rounded-lg text-xs"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        title="Fullscreen"
                      >
                        <Maximize2 size={14} />
                      </motion.button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </motion.div>
      </div>
      </div>

      {/* Fullscreen Video Modal */}
      <AnimatePresence>
        {selectedVideoIndex !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setSelectedVideoIndex(null)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="relative max-w-4xl max-h-full"
              onClick={(e) => e.stopPropagation()}
            >
              <video
                src={generatedVideos[selectedVideoIndex]?.url}
                className="max-w-full max-h-full rounded-2xl shadow-2xl"
                controls
                autoPlay
                loop
              />
              <button
                onClick={() => setSelectedVideoIndex(null)}
                className="absolute top-4 right-4 p-2 bg-black/50 backdrop-blur-md rounded-xl text-white hover:bg-black/70 transition-colors"
              >
                ✕
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}